import {Component, EventEmitter, Input, Output} from '@angular/core';
import {ModalComponent} from '../../../core/components/modal/modal.component';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Photographer} from '../../../marketplace/entities/photographer/photographer.entity';
import {Service} from '../../../marketplace/entities/photographer/service.entity';
import {ReactiveFormsModule, FormBuilder, Validators, FormGroup} from '@angular/forms';
import {NgClass, CurrencyPipe} from '@angular/common';
import {DateTimePickerComponent, DateTimeSelection} from '../../../marketplace/components/date-time-picker/date-time-picker.component';
import {PhotographerService as PhotographerServiceCard} from '../../../marketplace/components/photographer/photographer-service/photographer-service';
import {BookingService} from '../../../marketplace/services/booking/booking.service';
import {DateTime} from 'luxon';
import {ToastrService} from 'ngx-toastr';
import {AuthenticationService} from '../../../core/services/authentication/authentication.service';
import {User} from '../../../core/entities/user/user.entity';

@Component({
  selector: 'app-photographer-book-now-modal',
  imports: [
    ModalComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    DateTimePickerComponent,
    PhotographerServiceCard,
    NgClass,
    CurrencyPipe
  ],
  templateUrl: './photographer-book-now-modal.html',
  styleUrl: './photographer-book-now-modal.scss',
})
export class PhotographerBookNowModal {
    @Input() isOpen: boolean = false;
    @Input() photographer: Photographer|null = null;
    @Output() onClose = new EventEmitter();

    public form: FormGroup;
    public selectedService: Service|null = null;
    public selectedDateTime: DateTime|null = null;
    public isSubmitting = false;
    public locale: string = 'en';
    private readonly user: User|null = null;
    private emailBlurred = false;
    private readonly sectionOrder: SectionKey[] = ['service', 'datetime', 'customer'];
  public sectionState: Record<SectionKey, boolean> = {
    service: true,
    datetime: false,
    customer: false,
  };
  private noteBlurred = false;

    public constructor(
      private readonly fb: FormBuilder,
      private readonly bookingService: BookingService,
      private readonly transloco: TranslocoService,
      private readonly toastr: ToastrService,
      private readonly authenticationService: AuthenticationService,
    ) {
      this.user = this.authenticationService.getCurrentUser();
      this.locale = this.transloco.getActiveLang();
      this.form = this.fb.group({
        serviceId: [null, Validators.required],
        datetime: [null, Validators.required],
        firstname: [this.user?.firstname ?? '', Validators.required],
        lastname: [this.user?.lastname ?? '', Validators.required],
        email: [this.user?.email ?? '', [Validators.required, Validators.email]],
        note: ['', Validators.required],
      });

      this.form.statusChanges.subscribe(() => this.syncAutoClose());
    }

    public close(): void {
      this.resetForm();
      this.onClose.emit();
    }

    public onSelectService(service: Service): void {
      this.selectedService = service;
      this.form.patchValue({ serviceId: service.id });
      this.resetDateSelection();
      this.openNextSection('service');
      this.syncAutoClose();
    }

    public onDateSelected(_date: string): void {
      this.resetDateSelection();
    }

    public onDateTimeSelection(selection: DateTimeSelection): void {
      this.selectedDateTime = selection.dateTime;
      this.form.patchValue({ datetime: selection.dateTime });
      this.openNextSection('datetime');
      this.syncAutoClose();
    }

    public get selectedDateLabel(): string | null {
      if (!this.selectedDateTime) return null;
      return this.selectedDateTime.setLocale(this.locale).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
    }

    public get selectedTimeLabel(): string | null {
      if (!this.selectedDateTime) return null;
      return this.selectedDateTime.setLocale(this.locale).toLocaleString(DateTime.TIME_SIMPLE);
    }

    public submit(): void {
      if (!this.selectedService || !this.selectedDateTime || this.form.invalid) {
        this.form.markAllAsTouched();
        this.toastr.error(
          this.transloco.translate('marketplace.booking.create.select-date-form.errors.content'),
          this.transloco.translate('marketplace.booking.create.select-date-form.errors.title')
        );
        return;
      }

      const { firstname, lastname, email, note } = this.form.getRawValue();
      const price = this.getDisplayPrice(this.selectedService);

      this.isSubmitting = true;
      this.bookingService.createBooking(
        this.selectedService.id,
        email,
        firstname,
        lastname,
        price,
        note ?? null,
        this.selectedDateTime,
        null
      ).subscribe({
        next: (result) => {
          this.isSubmitting = false;
          if (typeof window !== 'undefined') {
            window.location.href = result.checkoutUrl;
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.toastr.error(
            this.transloco.translate('marketplace.booking.create.select-date-form.errors.content'),
            this.transloco.translate('marketplace.booking.create.select-date-form.errors.title')
          );
        }
      });
    }

    public get isSubmitDisabled(): boolean {
      return this.isSubmitting || !this.selectedService || !this.selectedDateTime || this.form.invalid;
    }

    public getDisplayPrice(service: Service|null): number|null {
      if (!service) return null;
      const base = service.priceRangeActive
        ? (service.priceRangeMinPublic ?? service.priceRangeMaxPublic ?? null)
        : (service.publicPrice ?? service.price ?? null);

      if (base === null) {
        return null;
      }

      if (service.discountActive && service.discount) {
        return base * (1 - service.discount / 100);
      }
      console.log(service);

      return base;
    }

    private resetDateSelection(): void {
      this.selectedDateTime = null;
      this.form.patchValue({ datetime: null });
    }

    private resetForm(): void {
      this.form.reset({
        serviceId: null,
        datetime: null,
        firstname: this.user?.firstname ?? '',
        lastname: this.user?.lastname ?? '',
        email: this.user?.email ?? '',
        note: '',
      });
      this.selectedService = null;
      this.selectedDateTime = null;
      this.isSubmitting = false;
      this.sectionState = { service: true, datetime: false, customer: false };
      this.emailBlurred = false;
      this.noteBlurred = false;
    }

    public isSectionOpen(section: SectionKey): boolean {
      return this.sectionState[section];
    }

    public isSectionComplete(section: SectionKey): boolean {
      if (section === 'service') {
        return !!this.selectedService;
      }
      if (section === 'datetime') {
        return !!this.selectedDateTime;
      }
      // customer
      const firstnameValid = this.form.get('firstname')?.valid ?? false;
      const lastnameValid = this.form.get('lastname')?.valid ?? false;
      const emailValid = (this.form.get('email')?.valid ?? false) && this.emailBlurred;
      const noteValid = (this.form.get('note')?.valid ?? false) && this.noteBlurred;
      return firstnameValid && lastnameValid && emailValid && noteValid;
    }

    public toggleSection(section: SectionKey): void {
      const willOpen = !this.sectionState[section];
      this.sectionState = {
        service: false,
        datetime: false,
        customer: false,
      };
      if (willOpen) {
        this.sectionState[section] = true;
      }
    }

    private openNextSection(current: SectionKey): void {
      const currentIndex = this.sectionOrder.indexOf(current);
      const nextSection = this.sectionOrder[currentIndex + 1] ?? null;
      if (nextSection) {
        this.sectionState = {
          service: false,
          datetime: false,
          customer: false,
        };
        this.sectionState[nextSection] = true;
      }
    }

    private closeAllSections(): void {
      this.sectionState = {
        service: false,
        datetime: false,
        customer: false,
      };
    }

    private syncAutoClose(): void {
      if (this.selectedService && this.selectedDateTime && this.isSectionComplete('customer')) {
        this.closeAllSections();
      }
    }

    public onEmailFocus(): void {
      this.emailBlurred = false;
    }

    public onEmailBlur(): void {
      this.emailBlurred = true;
      this.syncAutoClose();
    }

    public onNoteFocus(): void {
      this.noteBlurred = false;
    }

    public onNoteBlur(): void {
      this.noteBlurred = true;
      this.syncAutoClose();
    }
}

type SectionKey = 'service' | 'datetime' | 'customer';
